import type { ToolId } from "../types";
import type { DailyUsageRow, ToolState, UsageBlob } from "./format";

/**
 * Combine the blobs every machine published into one view.
 *
 * Isomorphic, like the format itself — this is what the browser runs after
 * decrypting, so it must not reach for SQLite or `node:*`.
 */

/** Where one machine's data came from and how current it is. */
export interface MachineSummary {
  machine: string;
  /** Newest export this machine published, epoch ms. */
  exportedAt: number;
  /** Calendar years it published, oldest first. */
  years: number[];
}

export interface MergedSnapshot {
  daily: DailyUsageRow[];
  /** Newest state per tool. Absent for tools no machine reported. */
  tools: Partial<Record<ToolId, ToolState>>;
  /** Provenance, newest push first — lets the UI say which machine is stale. */
  machines: MachineSummary[];
}

/**
 * Merge blobs from any number of machines and years.
 *
 * The machines normally own disjoint tools, so nothing collides. Two rules exist
 * for when that assumption breaks — which it does the moment someone points one
 * machine at both Claude accounts via `STATSAI_CLAUDE_WORK_DIR`:
 *
 * - **Daily rows** key on day + tool + model, and the newest export wins the key
 *   outright. Whole-row replacement, never field-wise blending: two machines with
 *   partial views of the same tool would otherwise produce a total that matches
 *   neither of them and looks entirely plausible.
 * - **Tool state** prefers the machine with the newer `syncedAt`, treating "never
 *   synced" as older than any real reading. Freshness, not export time, is what
 *   makes a quota bar trustworthy — a machine that pushed thirty seconds ago from
 *   a collector that has been failing for a day should not outrank one that
 *   measured successfully an hour ago.
 */
export function mergeBlobs(blobs: UsageBlob[]): MergedSnapshot {
  const daily = new Map<string, { row: DailyUsageRow; exportedAt: number }>();
  const tools = new Map<ToolId, { state: ToolState; exportedAt: number }>();
  const machines = new Map<string, MachineSummary>();

  // Sorted before merging so the result never depends on the order the caller
  // happened to fetch blobs in. Ties on `exportedAt` are routine, not exotic: a
  // machine stamps every year it publishes with one export time, so its own
  // blobs always tie. Machine and year break the tie, and every "wins" test
  // below is `>=`, so the last blob in this order wins deterministically.
  const ordered = [...blobs].sort(
    (a, b) =>
      a.exportedAt - b.exportedAt ||
      a.machine.localeCompare(b.machine) ||
      a.year - b.year,
  );

  for (const blob of ordered) {
    for (const row of blob.daily) {
      const key = `${row.day}|${row.tool}|${row.model ?? ""}`;
      const held = daily.get(key);
      if (!held || blob.exportedAt >= held.exportedAt) {
        daily.set(key, { row, exportedAt: blob.exportedAt });
      }
    }

    for (const state of blob.tools) {
      const held = tools.get(state.tool);
      if (!held || isFresher(state, blob.exportedAt, held.state, held.exportedAt)) {
        tools.set(state.tool, { state, exportedAt: blob.exportedAt });
      }
    }

    const m = machines.get(blob.machine);
    if (m) {
      m.exportedAt = Math.max(m.exportedAt, blob.exportedAt);
      if (!m.years.includes(blob.year)) m.years.push(blob.year);
    } else {
      machines.set(blob.machine, {
        machine: blob.machine,
        exportedAt: blob.exportedAt,
        years: [blob.year],
      });
    }
  }

  const mergedTools: Partial<Record<ToolId, ToolState>> = {};
  for (const [id, held] of tools) mergedTools[id] = held.state;

  return {
    daily: [...daily.values()]
      .map((h) => h.row)
      .sort((a, b) => a.day.localeCompare(b.day) || a.tool.localeCompare(b.tool)),
    tools: mergedTools,
    machines: [...machines.values()]
      .map((m) => ({ ...m, years: [...m.years].sort((a, b) => a - b) }))
      .sort((a, b) => b.exportedAt - a.exportedAt),
  };
}

/** Whether `next` describes a tool more recently than `held` does. */
function isFresher(
  next: ToolState,
  nextExportedAt: number,
  held: ToolState,
  heldExportedAt: number,
): boolean {
  // A real reading always beats "never synced", however recently the blob that
  // carried the empty state happened to be published.
  if (next.syncedAt !== null && held.syncedAt === null) return true;
  if (next.syncedAt === null && held.syncedAt !== null) return false;
  if (next.syncedAt !== null && held.syncedAt !== null) {
    if (next.syncedAt !== held.syncedAt) return next.syncedAt > held.syncedAt;
  }
  // Same freshness (or both unsynced): fall back to which blob is newer. `>=`
  // rather than `>` so that when those tie too, the caller's input order still
  // cannot decide the winner — `mergeBlobs` has already sorted, so "later" is
  // fully determined by (exportedAt, machine, year).
  return nextExportedAt >= heldExportedAt;
}
